import { Grid, useTheme } from "@mui/material";
import { ExternalLink } from "ExternalLink";
import { LinkItem } from "LinkItem";
import { useAnalytics } from "api/analytics";
import { useStores } from "api/stores";
import { LineChartOverview } from "components/GraphsSections/LineChartWithOverview/LineChartOverview";
import {
  currentDate,
  formatDate,
  formatDateToParse,
  getFormatByAggregationType,
  getStartDate,
} from "components/GraphsSections/LineChartWithOverview/utils";
import { TopHeader } from "components/Header/TopHeader/TopHeader";
import { ReactComponent as CircleIcon } from "components/Icons/circle.svg";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { useMessage } from "hooks/intl";
import { useManageInfiniteScroll } from "hooks/useManageInfiniteScroll";
import { sumBy, toNumber } from "lodash";
import { useEffect, useMemo, useState } from "react";
import {
  AggregationType,
  ChartInterval,
  ChartTab,
  GlobalOverviewData,
  GlobalOverviewMetrics,
  GlobalOverviewMetricsUnion,
} from "types/charts";
import { StoreData } from "types/stores";
import { OLDate } from "utilities/dateFormat";
import { links } from "utilities/links";
import { useStyles } from "./Analytics.styles";

const activeIntervalSettingsInitialValues: ChartInterval = {
  intervalLabel: "last30Days",
  aggregationType: AggregationType.DAY,
  intervalStartDate: getStartDate(currentDate, 30),
  intervalEndDate: formatDate(currentDate),
};

const maxVisibleStoreCount = 6;

export function Analytics() {
  const { message } = useMessage();
  const classes = useStyles();
  const theme: OLTheme = useTheme();

  const storeDataColors = [
    theme.primary500,
    theme.green500,
    theme.orange500,
    theme.red500,
    theme.white800,
    theme.purple,
  ];

  const [activeTab, setActiveTab] = useState<GlobalOverviewMetricsUnion>(
    GlobalOverviewMetrics.REGISTERED_MEMBERS,
  );
  const [activeIntervalSettings, setActiveIntervalSettings] = useState(
    activeIntervalSettingsInitialValues,
  );
  const [chartData, setChartData] =
    useState<(GlobalOverviewData & { color: string; storeName: string })[]>();
  const [selectedStores, setSelectedStores] = useState<StoreData[]>([]);
  const [storeSearch, setStoreSearch] = useState<string>();

  const { getGeneralOverviewBatchData } = useAnalytics();
  const { useInfiniteStoreList } = useStores();

  const {
    data: infiniteStoresList,
    size: storesSize,
    setSize: setStoresSize,
    isValidating,
  } = useInfiniteStoreList({
    filters: storeSearch
      ? [
          {
            condition: "like",
            query: "name",
            value: storeSearch,
          },
        ]
      : [],
    sortKey: "name",
    order: "asc",
  });

  const { infiniteData: storesList, anyElementsToFetch: anyStoresToFetch } =
    useManageInfiniteScroll(infiniteStoresList || []);

  const storesData = useMemo(() => storesList || [], [storesList]);
  const selectedStoresId = selectedStores.map((store) => store.storeId);

  const isOnlyOneStoreSelected = (storeId: string) =>
    selectedStores.length === 1 && selectedStoresId.includes(storeId);

  const maxNumberOfStoresIsSelected = (storeId: string) =>
    selectedStores.length === maxVisibleStoreCount &&
    !selectedStoresId.includes(storeId);

  const storesDropdown = {
    data: storesData.map((store) => ({
      disabled:
        maxNumberOfStoresIsSelected(store?.storeId) ||
        isOnlyOneStoreSelected(store?.storeId),
      ...store,
    })),
    selected: selectedStores.map((store) => ({
      id: store?.storeId,
      label: store?.name,
    })),
    anyToFetch: anyStoresToFetch,
    setTypeSearch: setStoreSearch,
    loadMore: () => !isValidating && setStoresSize(storesSize + 1),
    setSelectedStore: (store: StoreData) => {
      const storeHasAlreadyBeenSelected = selectedStores?.find(
        ({ storeId }) => storeId === store?.storeId,
      );

      if (storeHasAlreadyBeenSelected) {
        setSelectedStores(
          selectedStores?.filter(({ storeId }) => storeId !== store?.storeId),
        );
      } else {
        setSelectedStores([
          ...(Array.isArray(selectedStores) ? selectedStores : []),
          store,
        ]);
      }
    },
  };

  const initialStore = useMemo(() => storesList[0], [storesList]);

  const fetchAnalyticsData = async (storesId: string[]) => {
    setChartData([]);
    const storesAnalyticsData = await getGeneralOverviewBatchData(storesId, {
      filters: [
        {
          condition: "",
          query: "dataType",
          value: activeTab,
        },
        ...(activeIntervalSettings.aggregationType
          ? [
              {
                condition: "",
                query: "aggregationType",
                value: activeIntervalSettings.aggregationType,
              },
            ]
          : []),
        ...(activeIntervalSettings.intervalStartDate
          ? [
              {
                condition: "",
                query: "intervalStartDate",
                value: activeIntervalSettings.intervalStartDate,
              },
            ]
          : []),
        ...(activeIntervalSettings.intervalEndDate
          ? [
              {
                condition: "",
                query: "intervalEndDate",
                value: activeIntervalSettings.intervalEndDate,
              },
            ]
          : []),
      ],
    });

    setChartData(
      storesAnalyticsData?.map((data, index) => ({
        color: storeDataColors[index],
        storeName: selectedStores[index].name,
        ...data,
      })),
    );
  };

  const analyticsData = chartData || [];

  const chartTabs: ChartTab[] = analyticsData && [
    {
      name: GlobalOverviewMetrics.REGISTERED_MEMBERS,
      tooltip: {
        externalLink: {
          path: "https://help.openloyalty.io/main-features/dashboard#registered-members",
        },
        text: message(
          { id: "registeredMembersTooltip" },
          {
            link: (
              <ExternalLink path="https://help.openloyalty.io/main-features/dashboard#registered-members">
                {message({ id: "learnMore" })}
              </ExternalLink>
            ),
          },
        ),
      },
      value: sumBy(analyticsData, "header.registeredMembers"),
      formatNumber: true,
    },
    {
      name: GlobalOverviewMetrics.ACTIVE_MEMBERS,
      tooltip: {
        externalLink: {
          path: "https://help.openloyalty.io/main-features/dashboard/general-overview#active-members",
        },
        internalLink: {
          text: message({ id: "activeMemberSettings" }),
          to: links.membersConfiguration,
        },
        text: message(
          { id: "activeMembersTooltip" },
          {
            configuration: (
              <LinkItem blueSmall to={links.membersConfiguration}>
                {message({ id: "configuration" })}
              </LinkItem>
            ),
          },
        ),
      },
      value: sumBy(analyticsData, "header.activeMembers"),
      formatNumber: true,
    },
    {
      name: GlobalOverviewMetrics.TRANSACTIONS,
      tooltip: {
        externalLink: {
          path: "https://help.openloyalty.io/main-features/dashboard#transactions",
        },
        text: message(
          { id: "transactionsTooltip" },
          {
            link: (
              <ExternalLink path="https://help.openloyalty.io/main-features/dashboard#transactions">
                {message({ id: "learnMore" })}
              </ExternalLink>
            ),
          },
        ),
      },
      value: sumBy(analyticsData, "header.transactions"),
      formatNumber: true,
    },
    {
      name: GlobalOverviewMetrics.AVG_NUMBER_OF_TRANSACTIONS,
      tooltip: {
        externalLink: {
          path: "https://help.openloyalty.io/main-features/dashboard#avg.-number-of-transactions",
        },
        text: message(
          { id: "avgNumberOfTransactionsTooltip" },
          {
            link: (
              <ExternalLink path="https://help.openloyalty.io/main-features/dashboard#avg.-number-of-transactions">
                {message({ id: "learnMore" })}
              </ExternalLink>
            ),
          },
        ),
      },
      value: sumBy(analyticsData, "header.avgNumberOfTransactions"),
      formatCurrency: true,
    },
  ];

  const lineChartData =
    analyticsData &&
    analyticsData.map((data) => ({
      color: data.color,
      data: Object.keys(data?.data).map((key) => {
        const dateToParse = formatDateToParse(key, data?.aggregationType);

        return {
          x: Date.parse(dateToParse)
            ? OLDate(dateToParse, {
                formatString: getFormatByAggregationType(data?.aggregationType),
              })
            : "",
          y: toNumber(data?.data[key]),
        };
      }),
      id: data.storeName,
      legendIcon: <CircleIcon style={{ color: data.color, width: "10px" }} />,
      legendLabel: data.storeName,
      multiline: true,
    }));

  useEffect(() => {
    if (selectedStores.length === 0 && initialStore) {
      setSelectedStores([initialStore]);
    }
  }, [initialStore, selectedStores]);

  useEffect(() => {
    if (selectedStores.length > 0) {
      fetchAnalyticsData(selectedStores.map((store) => store.code));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedStores, activeIntervalSettings]);

  return (
    <>
      <TopHeader title={message({ id: "globalManagement" })} />
      <Grid container className={classes.gridContainer} mt={12}>
        <Grid className={classes.gridChild} item xs={12}>
          <LineChartOverview
            chartName="globalOverview"
            tabs={chartTabs}
            data={lineChartData}
            activeTab={activeTab}
            aggregationType={analyticsData[0]?.aggregationType}
            activeIntervalSettings={activeIntervalSettings}
            setActiveIntervalSettings={setActiveIntervalSettings}
            setActiveTab={setActiveTab}
            enableDownload
            storesDropdown={storesDropdown}
            isGlobalAnalytics={true}
          />
        </Grid>
      </Grid>
    </>
  );
}
